import React, { useState, useRef, useEffect } from 'react'
import { theme } from '../../styles/theme'
import { Send, Sparkles, Clock } from 'lucide-react'
import { Card } from '../common/Card'

interface EnhancedQueryBarProps {
  onSubmit: (query: string) => void
  loading?: boolean
  suggestions?: string[]
}

export const EnhancedQueryBar: React.FC<EnhancedQueryBarProps> = ({
  onSubmit,
  loading = false,
  suggestions = [],
}) => {
  const [query, setQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const defaultSuggestions = [
    'Which markets have the highest growth potential?',
    'What are the top expansion opportunities in Europe?',
    'Show me revenue trends by market',
    'Which products perform best in Germany?',
  ]

  const suggestionsToShow = suggestions.length > 0 ? suggestions : defaultSuggestions

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim() && !loading) {
      onSubmit(query.trim())
      setQuery('')
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
    setShowSuggestions(false)
    onSubmit(suggestion)
  }

  useEffect(() => {
    if (query.length > 0) {
      setShowSuggestions(true)
    }
  }, [query])

  return (
    <div style={{ position: 'relative', width: '80%', margin: '0 auto' }}>
      <Card
        style={{
          boxShadow: theme.ai.glow,
          border: `2px solid ${theme.colors.purple}`,
          padding: 0,
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            padding: theme.spacing[4],
            color: theme.colors.purple,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Sparkles size={24} />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask about expansion opportunities..."
            disabled={loading}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: theme.typography.fontSize.lg,
              fontFamily: theme.typography.fontFamily,
              backgroundColor: 'transparent',
              padding: `${theme.spacing[4]} 0`,
            }}
          />
          <button
            type="submit"
            disabled={!query.trim() || loading}
            style={{
              margin: theme.spacing[2],
              padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
              background: theme.ai.gradient,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              fontWeight: theme.typography.fontWeight.semibold,
              fontSize: theme.typography.fontSize.base,
              cursor: !query.trim() || loading ? 'not-allowed' : 'pointer',
              opacity: !query.trim() || loading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing[2],
              transition: theme.transitions.base,
            }}
          >
            {loading ? 'Processing...' : 'Ask AI'}
            {!loading && <Send size={18} />}
          </button>
        </form>
      </Card>

      {showSuggestions && query.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.xl,
            padding: theme.spacing[3],
            zIndex: theme.zIndex.dropdown,
          }}
        >
          <div style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing[3],
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
          }}>
            <Clock size={16} />
            Suggested queries
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
            {suggestionsToShow.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                style={{
                  padding: theme.spacing[3],
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textPrimary,
                  cursor: 'pointer',
                  transition: theme.transitions.fast,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.gray50
                  e.currentTarget.style.borderColor = theme.colors.purple
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.borderColor = theme.colors.border
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
